"use client";

/**
 * gallery renderer — responsive image grid with optional captions. Each card
 * uses its `image` field; missing images render as a placeholder.
 *
 * Data: section.content { title, subtitle, columns? } +
 *       section.cards [{ image, title }]
 */

import type { VisualCmsSection } from "@/types/visual-cms";
import { SECTION_CONTAINER, sectionShellStyle } from "./section-shell";

function readString(c: unknown, key: string): string {
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const v = (c as Record<string, unknown>)[key];
    return typeof v === "string" ? v : "";
  }
  return "";
}

function readColumns(c: unknown): 2 | 3 | 4 {
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const v = (c as Record<string, unknown>).columns;
    if (typeof v === "number" && [2, 3, 4].includes(v)) return v as 2 | 3 | 4;
  }
  return 3;
}

const gridByCols: Record<2 | 3 | 4, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

export function GallerySection({ section }: { section: VisualCmsSection }) {
  const shell = sectionShellStyle(section);
  const content = (section.content as Record<string, unknown>) ?? {};
  const title = readString(content, "title");
  const subtitle = readString(content, "subtitle");
  const cols = readColumns(content);
  const items = (section.cards ?? []).filter((c) => c.is_active !== false);

  return (
    <section className={shell.className} style={shell.style}>
      <div className={SECTION_CONTAINER}>
        {(title || subtitle) && (
          <div className="text-center max-w-2xl mx-auto mb-10">
            {title && (
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
                {title}
              </h2>
            )}
            {subtitle && <p className="mt-2 text-base text-gray-500">{subtitle}</p>}
          </div>
        )}

        <div className={`grid grid-cols-1 ${gridByCols[cols]} gap-4`}>
          {items.map((it) => (
            <figure key={it.id} className="overflow-hidden rounded-lg bg-gray-100">
              {it.image ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={it.image}
                  alt={it.title || ""}
                  className="w-full aspect-[4/3] object-cover"
                />
              ) : (
                <div className="w-full aspect-[4/3] flex items-center justify-center text-xs text-gray-400">
                  No image
                </div>
              )}
              {it.title && (
                <figcaption className="px-3 py-2 text-sm text-gray-700 bg-white border-t border-gray-200">
                  {it.title}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
